import "./styles/rich-text-editor.css";

import {css, StyleSheet, type CSSProperties} from "aphrodite";
import {useId} from "react";
import {Editable, ReactEditor, Slate} from "slate-react";

import {View, type PropsFor} from "@khanacademy/wonder-blocks-core";
import {
    border,
    font,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {
    renderElement,
    renderLeaf,
    renderPlaceholder,
} from "./slate/slate-renderers.tsx";
import {descendantsToMarkdown} from "./slate/slate-to-markdown.ts";
import {Toolbar} from "./toolbar.tsx";
import type {RichTextValidationError} from "./types.ts";
import {useInitEditor} from "./use-init-editor.ts";
import {useValidateValue} from "./use-validate-value.ts";

// TODO(@rgpass): Modify `validate` to string | null | void -- string is invalid, others if valid
// TODO(@rgpass): Remove the idea of `RichTextValidationError` and just use a string for the error message
// TODO(@rgpass): After we remove <ActivityCustomization />, remove the `displayLabel` prop
export interface Props {
    id: string;
    label: string;
    disableStickyToolbar?: boolean;
    displayLabel?: boolean;
    value: string;
    disabled?: boolean;
    placeholder?: string;
    validate: (text: string) => void;
    style?: CSSProperties;
    onChange?: (text: string) => void;
    error: RichTextValidationError;
}

type SlateProps = PropsFor<typeof Slate>;
type EditableProps = PropsFor<typeof Editable>;

const renderEditableElement: EditableProps["renderElement"] = (elementProps) =>
    renderElement({
        ...elementProps,
        showParagraphWarnings: false,
        isDebugModeActive: true,
    });

const renderEditableLeaf: EditableProps["renderLeaf"] = (leafProps) =>
    renderLeaf({
        ...leafProps,
        showParagraphWarnings: false,
    });

// NOTE(@rgpass): If making major changes to this component, ensure all
// mocks are updated. Global search for `rich-text-editor`, () => ` to find
// all the mocks.
export default function RichTextEditor(props: Props) {
    const {
        id,
        label,
        displayLabel = true,
        disableStickyToolbar = false,
        value,
        disabled,
        placeholder,
        validate,
        style,
        onChange,
        error,
    } = props;
    const {editor} = useInitEditor({defaultValue: value});

    useValidateValue({editor, value, error, validate});

    const baseId = useId();
    const ids = {
        error: `${baseId}-error`,
        editor: `${baseId}-editor`,
        label: `${baseId}-label`,
    };

    const handleLabelClick = () => {
        ReactEditor.focus(editor);
    };

    const handleChange: SlateProps["onChange"] = (editorChildren) => {
        const {result: updatedText} = descendantsToMarkdown(editorChildren);

        if (updatedText === value) {
            return;
        }

        onChange?.(updatedText);

        if (error.type === "INVALID") {
            validate(updatedText);
        }
    };

    const isEmpty = value.trim().length === 0;

    return (
        <View style={style}>
            {displayLabel && (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
                <LabelLarge
                    htmlFor={id}
                    id={ids.label}
                    onClick={handleLabelClick}
                    tag="label"
                >
                    {label}
                </LabelLarge>
            )}
            <div>
                <Slate
                    editor={editor}
                    initialValue={editor.children}
                    onChange={handleChange}
                >
                    <Toolbar
                        disabled={disabled}
                        disableStickyToolbar={disableStickyToolbar}
                    />

                    <Editable
                        aria-describedby={
                            error.type === "INVALID" ? ids.error : undefined
                        }
                        aria-disabled={disabled ? "true" : undefined}
                        aria-invalid={
                            error.type === "INVALID" ? "true" : undefined
                        }
                        // If we're displaying a label, use the label ID for aria-labelledby
                        aria-labelledby={displayLabel ? ids.label : undefined}
                        // If we're not displaying a label, use the label text as the aria-label
                        aria-label={!displayLabel ? label : undefined}
                        className={`rich-text-editor ${css(
                            styles.instructionsTextarea,
                            error.type === "INVALID" &&
                                styles.instructionsTextareaError,
                        )}`}
                        id={id}
                        onBlur={() => validate(value)}
                        // NOTE(@rgpass): This prevents the placeholder from being formatted when the user
                        // selects a rich text element (ex. bold, italic, etc) before typing anything.
                        placeholder={isEmpty ? placeholder : undefined}
                        // TODO: Consider using aria-disabled instead
                        readOnly={disabled}
                        renderElement={renderEditableElement}
                        renderLeaf={renderEditableLeaf}
                        renderPlaceholder={renderPlaceholder}
                    />
                </Slate>
            </div>
            {error.type === "INVALID" && (
                <LabelSmall id={ids.error} style={styles.errorText}>
                    {error.msg}
                </LabelSmall>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: semanticColor.core.foreground.critical.subtle,
    },
    instructionsTextarea: {
        // NOTE: Without these properties, the font was
        // ever-so-slightly bolder than the SingleSelects above. Definitely not
        // a big deal, but I was curious and this fixed it.
        "-moz-osx-font-smoothing": "grayscale",
        "-webkit-font-smoothing": "antialiased",
        background: semanticColor.input.default.background,
        border: `${border.width.thin} solid ${semanticColor.input.default.border}`,
        borderRadius: `0 0 4px 4px`,
        color: semanticColor.input.default.foreground,
        fontFamily: font.family.sans,
        fontSize: font.size.medium,
        height: 130,
        outlineColor: semanticColor.focus.outer, // Matches SingleSelect
        overflowY: "auto",
        paddingBlock: 10, // Matches SingleSelect
        paddingInline: spacing.medium_16, // Matches SingleSelect
        resize: "vertical",
    },
    instructionsTextareaError: {
        background: semanticColor.input.error.background,
        borderColor: semanticColor.input.error.border,
    },
});

export type {RichTextValidationError};
